import * as React from "react";
import { toast } from "sonner";
import { RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClientFactory } from "@/main/common/ClientFactory";
import { Localizer } from "@/main/react/Types";
import { logApiFailure } from "@/main/common/ApiUtils";
import { showYesNoDialog } from "@/main/react/Dialog/YesNoDialog";

/**
 * Wraps one card on a "deleted" dashboard sub-tab with the control that brings it back.
 *
 * The Restore affordance lives HERE rather than inside `DiscussionCardDisplayer` /
 * `ThoughtCardDisplayer` on purpose. Those two render on the feed, search, series, group and profile
 * surfaces; a deleted-state control added inside them would be one prop default away from appearing on
 * all of those. The dashboard's deleted sub-tab is the only place the control is correct, so the
 * dashboard is what supplies it.
 *
 * Added 2026-08-12 with the author-restore verbs. Design:
 * `designs/01-now/2026-08-12-author-recoverable-deleted-content-design.md`.
 *
 * <p><b>Reshaped 2026-08-14 (EMPTY-STATES-6).</b> Two defects, one structural and one behavioural:
 *
 * <ul>
 *   <li><b>The control belonged to no card.</b> This wrapper was a bare `flex flex-col gap-2` and
 *       `MainPageListStack` separates rows with the same `gap-2`, so the gap ABOVE the Restore pill
 *       equalled the gap BELOW it — measured 9px/9px on desktop and 22 image px each side on a Pixel 7,
 *       in both themes, on every row. It floated on the page background exactly halfway between the
 *       card it restores and the next one. That is equal by construction, so moving the button alone
 *       could not fix it: the group needs a visible boundary, and the gap INSIDE the group has to be
 *       smaller than the gap between groups.</li>
 *   <li><b>It fired with no confirmation</b>, and restoring republishes a post the member deliberately
 *       took down — outward-facing, and undoable only by deleting it again.</li>
 * </ul>
 */
export type DeletedContentKind = "discussion" | "thought";

type Props = {
    publicId: string;
    kind: DeletedContentKind;
    localizer: Localizer;
    /** Called once the server has confirmed the restore, so the row can leave the deleted list. */
    onRestored: (publicId: string) => void;
    /**
     * What this row restores, in the member's own words (`EMPTY-STATES-80`, wave-12 sheet Q13).
     *
     * <p>A post's title; for a thought, a short plain-text excerpt of its body, because a thought has
     * no title and Q13 chose the excerpt over inventing a new vocabulary for one. Built by the CALLER,
     * which is the only place the object's name exists: both call sites used to pass `publicId`,
     * `kind`, `localizer` and `onRestored` and nothing else, which is why this looked like a
     * single-file fix for as long as it did.
     *
     * <p>Optional, and everything degrades to the previous static wording without it — the tray is
     * also rendered by the component gallery, and a name is a thing only a real list has.
     */
    name?: string;
    children: React.ReactNode;
};

export function DeletedContentRestoreRow({ publicId, kind, localizer, onRestored, name, children }: Props) {
    const trimmedName = name?.trim() || null;
    const [restoring, setRestoring] = React.useState(false);

    const submitRestore = React.useCallback(async () => {
        setRestoring(true);
        try {
            if (kind === "discussion") {
                await ClientFactory.restoreOwnDeletedDiscussion(publicId);
            } else {
                await ClientFactory.restoreOwnDeletedThought(publicId);
            }
            toast.success(localizer(kind === "discussion"
                ? "dashboard.deleted.restore.post_success"
                : "dashboard.deleted.restore.thought_success"));
            // Only after the server confirms. An optimistic removal here would take the row out of the
            // one list that can still reach it, which is the opposite of what this feature is for.
            onRestored(publicId);
        } catch (error) {
            logApiFailure("[DeletedContentRestoreRow] restore failed", error);
            toast.error(localizer("dashboard.deleted.restore.failed"));
            setRestoring(false);
        }
    }, [kind, localizer, onRestored, publicId]);

    const handleRestore = React.useCallback(() => {
        if (restoring) {
            return;
        }
        showYesNoDialog({
            localizer,
            title: localizer(kind === "discussion"
                ? "dashboard.deleted.restore.confirm.post_title"
                : "dashboard.deleted.restore.confirm.thought_title"),
            // `EMPTY-STATES-80` — the object, named, above the static consequence.
            //
            // The name is rendered VERBATIM as its own line rather than interpolated into a sentence,
            // and that is the whole reason this half costs no localization key: a title or an excerpt
            // is already the member's own words in the member's own language, so there is nothing to
            // translate and nothing to get wrong in 25 catalogs. The confirmation exists to catch the
            // most likely mistake on this screen — the WRONG ROW, three visually identical Restore
            // pills — and it could not catch it while it never said which post it was about.
            message: (
                <div className="flex flex-col gap-2">
                    {trimmedName ? (
                        <p
                            className="font-medium text-foreground"
                            data-playwright-id="restore-deleted-content-name"
                        >
                            {trimmedName}
                        </p>
                    ) : null}
                    <p>{localizer("dashboard.deleted.restore.confirm.message")}</p>
                </div>
            ),
            yesLabel: localizer("dashboard.deleted.restore.action"),
            yesIcon: RotateCcw,
            yesPlaywrightId: "confirm-restore-deleted-content",
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: submitRestore,
        });
    }, [kind, localizer, restoring, submitRestore, trimmedName]);

    return (
        // The boundary EMPTY-STATES-6 asks for: a tray around the card and its action, so the two read
        // as one object. The inner gap is 4px (`gap-1`) while the distance to the next group is the
        // tray's own 6px padding twice over PLUS `MainPageListStack`'s 8px — visibly larger, which is
        // the constraint the row states. Elevation is the hairline border and a quiet fill, per the
        // design language (borders, not shadows). The card keeps its own border and radius untouched:
        // `FEED_CARD_ROOT_CLASS` is pinned by `feedCardBoxParity.spec.ts`, so the grouping is drawn
        // AROUND the card rather than by reshaping it.
        <div
            className="flex flex-col gap-1 rounded-2xl border border-border/60 bg-muted/30 p-1.5"
            data-playwright-id="deleted-content-row"
            data-public-id={publicId}
        >
            {children}
            <div className="flex justify-end px-1 pb-0.5">
                {/* `EMPTY-STATES-40`: `size="sm"` is `h-8` (32 CSS px) and only `icon-touch` carries a
                    coarse floor, so the one control that UNDOES a deletion was the smallest target on
                    the route. The same mechanism `SensitivePageGate` took for `PROFILE-7` — a minimum
                    rather than a fixed height, so a long localized label still wraps, and gated on the
                    input device rather than a breakpoint. The expander is wrong here: this control is
                    alone in its row, but a centred 44px slug on a 32px pill would overhang the row
                    above, whose own controls sit inside the card. */}
                {/* `EMPTY-STATES-80`, the a11y half. The visible label stays the bare verb — it sits
                    inside a tray with exactly one card, so a sighted reader gets the object from
                    proximity — but a screen-reader user tabbing the page heard "Restore, button" three
                    times over with nothing distinguishing them, and the confirmation did not recover
                    the information either. The accessible name now carries the object.

                    An `aria-label` overrides the visible text, so it is set ONLY when there is a name
                    to carry; without one the button keeps the announcement it has always had rather
                    than gaining an empty or duplicated one. */}
                <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full touch-manipulation [@media(pointer:coarse)]:min-h-11"
                    disabled={restoring}
                    onClick={handleRestore}
                    aria-label={trimmedName
                        ? localizer("dashboard.deleted.restore.action.named", trimmedName)
                        : undefined}
                    data-playwright-id="restore-deleted-content"
                >
                    <RotateCcw className="size-4" aria-hidden="true" />
                    {localizer("dashboard.deleted.restore.action")}
                </Button>
            </div>
        </div>
    );
}
